import {Injectable} from '@angular/core';
import { HttpHeaders, HttpParams, HttpClient, HttpResponse} from '@angular/common/http';
import {SubmissionRecordObject, SampleSearchRecordObject, SampleDetailsRecordObject, TestRecordDataObject
} from './submission-history-object';
import {
    SubmissionSearchObject,
    SampleSearchObject,
    SubmissionSearchUserInfoObject
} from '../../components/dashboard/search-object';
import {BehaviorSubject, firstValueFrom} from 'rxjs';
import {Extract} from './extract-service';
import {PaginationObject} from '../../components/dashboard/pagination-object';
import { ErrorDetails } from '../../components/dashboard/errors/errordetails';
import { createHttpParams } from './http-params';
import {environment} from '../../../environments/environment';

@Injectable()
export class SubmissionHistoryService {

    private submissionTable = new BehaviorSubject<SubmissionRecordObject[]>(null);
    currentSubmissionTable = this.submissionTable.asObservable();

    private sampleSearchTable = new BehaviorSubject<SampleSearchRecordObject[]>(null);
    currentSampleSearchTable = this.sampleSearchTable.asObservable();

    private sampleTable = new BehaviorSubject<SampleSearchRecordObject[]>(null);
    currentSampleTable = this.sampleTable.asObservable();

    private sampleSearchCriteria = new BehaviorSubject<SampleSearchObject>(null);
    currentSampleSearchCriteria = this.sampleSearchCriteria.asObservable();

    private sampleSubmissionData = new BehaviorSubject<SubmissionRecordObject>(null);
    currentSampleSubmissionData = this.sampleSubmissionData.asObservable();

    private userInfo = new BehaviorSubject<SubmissionSearchUserInfoObject>(null);
    currentUserInfo = this.userInfo.asObservable();


    private searchCriteria = new BehaviorSubject<SubmissionSearchObject>(null);
    currentSearchCriteria = this.searchCriteria.asObservable();

    private totalCount = new BehaviorSubject<number>(null);
    currentTotalCount = this.totalCount.asObservable();

    private pagination = new BehaviorSubject<PaginationObject>(null);
    currentPagination = this.pagination.asObservable();

    private samplePagination = new BehaviorSubject<PaginationObject>(null);
    currentSamplePagination = this.samplePagination.asObservable();

    private errorDetails = new BehaviorSubject<ErrorDetails[]>(null);
    currentErrorDetails = this.errorDetails.asObservable();

    private sampleErrorDetails = new BehaviorSubject<ErrorDetails[]>(null);
    currentSampleErrorDetails = this.sampleErrorDetails.asObservable();

    private sampleDetailsTable = new BehaviorSubject<SampleDetailsRecordObject[]>(null);
    currentSampleDetailsTable = this.sampleDetailsTable.asObservable();

    private testSubmissionData = new BehaviorSubject<TestRecordDataObject>(null);
    currentTestSubmissionData = this.testSubmissionData.asObservable();

    private urlPrefix = environment.is_local ? 'http://localhost:8081' : '';

    constructor(private http: HttpClient) {
        this.pagination.next(this.buildPagination(100));
        this.samplePagination.next(this.buildPagination(500));
        const searchObjectInit = new SubmissionSearchObject;
        searchObjectInit.submissionIdentifierType = 'fileId';
        this.changeSearchCriteria(searchObjectInit);
        this.changeSampleSearchCriteria(new SampleSearchObject());
    }

    // Clears data from the UI on logout so that the next user doesn't accidentally see it
    public clearUserData(): void {
        // Note: At this point only a subset of the user data is cleared on logout. Other data might
        // need to be cleared in the future based on observed behavior and/or security requirements.
        this.changeMessage(null);
        this.clearSubmissionPagination(0);
        this.changeUserInfo(new SubmissionSearchUserInfoObject());
        this.changeSearchCriteria(new SubmissionSearchObject());
        this.changeSampleSearchCriteria(new SampleSearchObject());
    }

    // Reload on login (reverse the effect of clearUserData)
    public reloadUserData(): void {
        this.getUserSearchInfo().then();
    }

    getSessionTableContent() {
        const url = this.urlPrefix + `/dashboard/submissions`;

        const pc: PaginationObject = this.pagination.getValue();

        return firstValueFrom(this.http.get(url, {
            params: new HttpParams()
                .set('page', pc.currentPage.toString())
                .set('results-per-page', pc.pageSize.toString())
            }))
            .then(resp => this.changeMessage(this.toFileRecordObjectList(resp)))
            .catch(this.handleError);
    }

    getSampleSubmissionData(uuid: string) {
        const url = this.urlPrefix + `/dashboard/sampleSubmissionData/${uuid}`;
        return firstValueFrom(this.http.get(url, {}))
            .then(resp => this.changeSampleSubmissionData(this.toFileRecordObject(resp)))
            .catch(this.handleError);
    }

    getTestSubmissionData(uuid: string, sampleId: string) {
        const url = this.urlPrefix + `/dashboard/sampleSubmissionData/${uuid}/testSubmissionData/${sampleId}`;
        return firstValueFrom(this.http.get(url, {}))
            .then(resp => this.changeTestSubmissionData(this.toTestRecordDataObject(resp)))
            .catch(this.handleError);
    }

    getSessionTableExportContent() {
        const url = this.urlPrefix + `/dashboard/export/submissions`;
        const pc: PaginationObject = this.pagination.getValue();
        return firstValueFrom(this.http.get(url, {
            params: new HttpParams()
                .set('results-per-page', `${pc.totalCount}`), responseType: 'text'
            }))
            .then(function (resp) {
                // new Extract().specialMessages(resp);
                new Extract().toCsv(resp, 'submission-history.csv')
            })
            .catch(this.handleError);
    }

    getSubmissionSamplesExportContent( uuid: string ) {

        const todayString = this.getExtractFileName();

        const url = this.urlPrefix + `/dashboard/export/submissionSamples`;
        return firstValueFrom(this.http.get(url, {
            params: new HttpParams()
            .set('uuid', `${uuid}`), responseType: 'text'
            }))
            .then(function (resp) {
                new Extract().toCsv(resp, 'samples_' + uuid + '_' + todayString + '.csv')
            })
            .catch(this.handleError);
    }

    getSamplesSearchExportContent() {

        if ( this.sampleSearchTable.value === null ) {
            return;
        }
        let params: HttpParams = createHttpParams(this.sampleSearchCriteria.value);

        const startDateTime = (document.querySelector('#startDate') as HTMLInputElement).value
            + ' ' + (document.querySelector('#startTime') as HTMLInputElement).value;
        const endDateTime = (document.querySelector('#endDate') as HTMLInputElement).value
            + ' ' + (document.querySelector('#endTime') as HTMLInputElement).value;

        if ( startDateTime.trim() !== '' ) {
            params = params.set('startDate', startDateTime)
        }
        if ( endDateTime.trim() !== '' ) {
            params = params.set('endDate', endDateTime)
        }

        const todayString = this.getExtractFileName();

        const url = this.urlPrefix + `/dashboard/export/sampleSearch`;
        return firstValueFrom(this.http.get(url, {params, responseType: 'text'}))
            .then(function (resp) {
                new Extract().toCsv(resp, 'samples_search_' + todayString + '.csv')
            })
            .catch(this.handleError);
    }

    getExtractFileName() {
        const today = new Date();
        const dd = today.getDate();
        const mm = today.getMonth() + 1; // January is 0!
        const yyyy = today.getFullYear();
        return mm + '_' + dd + '_' + yyyy;
    }


    getTotalCount(): Promise<any> {
        const url = this.urlPrefix + `/dashboard/submissions/total`;
        return firstValueFrom(this.http.get(url, {}))
            .then(resp => this.updatePagination(Number(resp).valueOf()))
            .catch(this.handleError);
    }

    getUserSearchInfo() {
        const url = this.urlPrefix + `/dashboard/dashboardUserInfo`;
        return firstValueFrom(this.http.get(url, {}))
            .then(resp => this.changeUserInfo(resp))
            .catch(this.handleError);
    }

    getSearchTableContent(search: SubmissionSearchObject, startDate: string, endDate: string, page: number) {
        const url = this.urlPrefix + `/dashboard/submissionSearch`;
        this.changeSearchCriteria(search);
        const pc: PaginationObject = this.pagination.getValue();

        let params: HttpParams = createHttpParams(search);

        if ( startDate !== '' ) {
            params = params.set('startDate', startDate)
        }
        if ( endDate !== '' ) {
            params = params.set('endDate', endDate)
        }
        params = params.set('results-per-page', `${pc.pageSize}`)

        params = params.set('page', page.toString())

        return firstValueFrom(this.http.get(url, {params}))
            .then(resp => this.changeMessage(this.toFileRecordObjectList(resp)))
            .catch(this.handleError);
    }

    getSearchTableExportContent(search: SubmissionSearchObject, startDate: string, endDate: string, page: number) {
        const url = this.urlPrefix + `/dashboard/export/submissionSearch`;
        const pc: PaginationObject = this.pagination.getValue();

        let params: HttpParams = createHttpParams(search);

        if ( startDate !== '' ) {
            params = params.set('startDate', startDate)
        }
        if ( endDate !== '' ) {
            params = params.set('endDate', endDate)
        }
        params = params.set('results-per-page', `${pc.pageSize}`)

        return firstValueFrom(this.http.get(url, {params, responseType: 'text'}))
            .then(function (resp) {
                // new Extract().specialMessages(resp);
                new Extract().toCsv(resp, 'submission-history.csv')
            })
            .catch(this.handleError);
    }

    getSearchTableContentCount(search: SubmissionSearchObject, startDate: string, endDate: string) {
        const url = this.urlPrefix + `/dashboard/submissionSearch/total`;
        this.changeSearchCriteria(search);

        let params: HttpParams = createHttpParams(search);

        if ( startDate !== '' ) {
            params = params.set('startDate', startDate)
        }
        if ( endDate !== '' ) {
            params = params.set('endDate', endDate)
        }

        return firstValueFrom(this.http.get(url, {params}))
            .then(resp => this.updatePagination(Number(resp).valueOf()))
            .catch(this.handleError);
    }

    getSampleTableContent(uuid: string) {
        const url = this.urlPrefix + `/dashboard/samples`;
        return firstValueFrom(this.http
            .get(url, { params: { docid_guid: uuid } }))
            .then(resp =>
                this.changeSampleTable(
                    this.toSampleSearchRecordObjectList(resp)
                )
            )
            .catch(this.handleError);
    }

    getSampleSearchTableContent(search: SampleSearchObject, startDate: string, endDate: string, page: number) {
        const url = this.urlPrefix + `/dashboard/sampleSearch`;
        this.changeSampleSearchCriteria(search);

        let params: HttpParams = createHttpParams(search);

        if ( startDate !== '' ) {
            params = params.set('startDate', startDate)
        }
        if ( endDate !== '' ) {
            params = params.set('endDate', endDate)
        }
        params = params.set('page', page.toString())

        return firstValueFrom(this.http.get(url, {params}))
            .then(resp => this.changeSampleSearchTable(this.toSampleSearchRecordObjectList(resp)))
            .catch(this.handleError);
    }

    getSampleSearchTableContentCount(search: SampleSearchObject, startDate: string, endDate: string) {
        const url = this.urlPrefix + `/dashboard/sampleSearch/total`;
        this.changeSampleSearchCriteria(search);

        let params: HttpParams = createHttpParams(search);

        if ( startDate !== '' ) {
            params = params.set('startDate', startDate)
        }
        if ( endDate !== '' ) {
            params = params.set('endDate', endDate)
        }

        return firstValueFrom(this.http.get(url, {params}))
            .then(resp => this.updateSamplePagination(Number(resp).valueOf()))
            .catch(this.handleError);
    }

    getSampleDetailsTableContent(uuid: string, sampleId: string) {
        const url = this.urlPrefix + `/dashboard/submissions/${uuid}/samples/${sampleId}`;
        return firstValueFrom(this.http
            .get(url, {}))
            .then(resp =>
                this.changeSampleDetailsTable(
                    this.toSampleDetailsRecordObjectList(resp)
                )
            )
            .catch(this.handleError);
    }


    getErrors(uuid: any): Promise<ErrorDetails[]> {
        const url = this.urlPrefix + `/dashboard/submissions/${uuid}/errors`;
        return firstValueFrom(this.http.get(url, {}))
            .then(resp => this.changeErrorDetails(this.toErrorDetailsObjectList(resp)))
            .catch(this.handleError);
    }


    getSampleErrors(uuid: string, sampleId: string): Promise<ErrorDetails[]> {
        const url = this.urlPrefix + `/dashboard/submissions/${uuid}/samples/${sampleId}/errors`;
        return firstValueFrom(this.http.get(url, {}))
            .then(resp => this.changeSampleErrorDetails(this.toErrorDetailsObjectList(resp)))
            .catch(this.handleError);
    }

    changeErrorDetails(errors: ErrorDetails[]) {
        this.errorDetails.next(errors);
    }


    changeSampleErrorDetails(errors: ErrorDetails[]) {
        this.sampleErrorDetails.next(errors);
    }


    toErrorDetailsObjectList(jsonErrorList: any): ErrorDetails[] {
        const errorDetailsList: ErrorDetails[] = new Array<ErrorDetails>();
        for (const error of jsonErrorList) {
            errorDetailsList.push(error);
        }
        return errorDetailsList;
    }


    resolve(checked: boolean, uuid: any): Promise<any> {
        const url = this.urlPrefix + `/dashboard/submissions/${uuid}/setResolution/` + checked;
        return firstValueFrom(this.http.put(url, {}))
            .then(resp => resp)
            .catch(this.handleError);
    }

    review(checked: boolean, uuid: any): Promise<any> {
        const url = this.urlPrefix + `/dashboard/submissions/${uuid}/setTCReview/` + checked;
        return firstValueFrom(this.http.put(url, {}))
            .then(resp => resp)
            .catch(this.handleError);
    }

    sampleSearchReview(checked: boolean, docidGuid: any, sampleId: any): Promise<any> {
        const url = this.urlPrefix + `/dashboard/submissions/${docidGuid}/${sampleId}/setSampleSearchReview/` + checked;
        return firstValueFrom(this.http.put(url, {}))
            .then(resp => resp)
            .catch(this.handleError);
    }

    getSubmissionText(uuid: string): Promise<HttpResponse<string>> {
        const url = this.urlPrefix + `/dashboard/submissions/${uuid}/source`;
        return firstValueFrom(this.http.get(url, {observe: 'response', responseType: 'text'}))
            .catch(this.handleError);
    }


    getSampleSource(uuid: string, sampleId: string) {
        const url = this.urlPrefix + `/dashboard/submissions/${uuid}/samples/${sampleId}/source`;
        return firstValueFrom(this.http.get(url, {responseType: 'text'}))
            .catch(this.handleError);
    }

    getFhirSampleSource(uuid: string, sampleId: string) {
        const url = this.urlPrefix + `/dashboard/submissions/${uuid}/samples/${sampleId}/fhirsource`;
        return firstValueFrom(this.http.get(url, {responseType: 'text'}))
            .catch(this.handleError);
    }

    resubmitSubmission(submissionXml: string): Promise<HttpResponse<string>> {
        const httpOptions = {
            headers: new HttpHeaders({
              'Content-Type':  'text/plain' // <- To SEND XML
            })
          };
          return firstValueFrom(this.http.post('/dashboard/resubmit/submit', submissionXml, httpOptions))
          .catch(this.handleError)
    }

    reprocessSubmission(uuid: string): Promise<HttpResponse<string>> {
        const httpOptions = {
            headers: new HttpHeaders({
              'Content-Type':  'text/plain' // <- To SEND XML
            })
          };
          return firstValueFrom(this.http.post('/dashboard/resubmit/reprocess', uuid, httpOptions))
          .catch(this.handleError)
    }

    private handleError(error: any): Promise<any> {
        console.error('An error occurred', error);
        return Promise.reject(error.message || error);
    }

    toFileRecordObjectList(jsonFileRecordList: any): SubmissionRecordObject[] {
        const fileRecordObjectList: SubmissionRecordObject[] = new Array<SubmissionRecordObject>();
        for (const jsonFileRecord of jsonFileRecordList) {
            fileRecordObjectList.push(jsonFileRecord);
        }
        return fileRecordObjectList;
    }

    toFileRecordObject(jsonFileRecord: any): SubmissionRecordObject {
        return jsonFileRecord;
    }

    toSampleSearchRecordObjectList(jsonFileRecordList: any): SampleSearchRecordObject[] {
        const fileRecordObjectList: SampleSearchRecordObject[] = new Array<SampleSearchRecordObject>();
        for (const jsonFileRecord of jsonFileRecordList) {
            fileRecordObjectList.push(jsonFileRecord);
        }
        return fileRecordObjectList;
    }


    toSampleDetailsRecordObjectList(jsonFileRecordList: any): SampleDetailsRecordObject[] {
        const fileRecordObjectList: SampleDetailsRecordObject[] = new Array<SampleDetailsRecordObject>();
        for (const jsonFileRecord of jsonFileRecordList) {
            fileRecordObjectList.push(jsonFileRecord);
        }
        return fileRecordObjectList;
    }

    toTestRecordDataObject(jsonFileRecordList: any): TestRecordDataObject {
        return jsonFileRecordList;
    }

    changeMessage(message: SubmissionRecordObject[]) {
        this.submissionTable.next(message);
    }

    changeSampleSearchTable(message: SampleSearchRecordObject[]) {
        this.sampleSearchTable.next(message);
    }

    clearSampleSearchTable() {
        this.sampleSearchTable.next(null);
    }

    changeSampleTable(message: SampleSearchRecordObject[]) {
        this.sampleTable.next(message);
    }

    clearSampleTable() {
        this.sampleTable.next(null);
    }

    changeSampleDetailsTable(message: SampleDetailsRecordObject[]) {
        this.sampleDetailsTable.next(message);
    }

    clearSampleDetailsTable() {
        this.sampleDetailsTable.next(null);
    }

    changeSampleSubmissionData(message: SubmissionRecordObject) {
        this.sampleSubmissionData.next(message);
    }

    clearSampleSubmissionData() {
        this.sampleSubmissionData.next(null);
    }

    changeTestSubmissionData(message: TestRecordDataObject) {
        this.testSubmissionData.next(message);
    }

    clearTestSubmissionData() {
        this.testSubmissionData.next(null);
    }

    changeUserInfo(message: SubmissionSearchUserInfoObject) {
        // TODO : Don't hard code on web-tier (okay with business in the short term)
        message.loci = ['HLA-A', 'HLA-B', 'HLA-C', 'HLA-DRB1', 'HLA-DQB1',
                        'HLA-DRB3', 'HLA-DRB4', 'HLA-DRB5', 'HLA-DPB1', 'HLA-DQA1', 'HLA-DPA1'];
        this.userInfo.next(message);
    }

    changeSearchCriteria(message: SubmissionSearchObject) {
        this.searchCriteria.next(message);
    }

    changeSampleSearchCriteria(message: SampleSearchObject) {
        this.sampleSearchCriteria.next(message);
    }

    clearSampleSearchCriteria() {
        this.sampleSearchCriteria.next(new SampleSearchObject);
    }

    changePagination(message: PaginationObject) {
        this.pagination.next(message);
    }

    updatePagination(totalCount: number) {
        const pc: PaginationObject = this.pagination.getValue();
        pc.totalCount = totalCount;
        this.changePagination(pc);
    }

    changeSamplePagination(message: PaginationObject) {
        this.samplePagination.next(message);
    }

    updateSamplePagination(totalCount: number) {
        const pc: PaginationObject = this.samplePagination.getValue();
        pc.totalCount = totalCount;
        this.changeSamplePagination(pc);
    }

    clearSamplePagination(totalCount: number) {
        const pc: PaginationObject = this.samplePagination.getValue();
        pc.currentPage = 0;
        pc.totalCount = totalCount;
        this.changeSamplePagination(pc);
    }

    clearSubmissionPagination(totalCount: number) {
        const pc: PaginationObject = this.pagination.getValue();
        pc.currentPage = 0;
        pc.totalCount = totalCount;
        this.changePagination(pc);
    }

    buildPagination(pageSize: number) {
        const pagination = new PaginationObject();
        pagination.currentPage = 0;
        pagination.pageSize = pageSize;
        return pagination;
    }

    public getNotes(uuid: string) {
        const url = this.urlPrefix + `/dashboard/submissions/${uuid}/notes`;
        return firstValueFrom(this.http.get(url, {responseType: 'text'}))
            .catch(this.handleError);
    }


    public saveNotes(uuid: string, noteText: string): Promise<Response> {
        const url = this.urlPrefix + `/dashboard/submissions/${uuid}/notes`;
        return firstValueFrom(this.http.put(url, noteText, {headers: new HttpHeaders()
                .set('Content-Type', 'text/plain')
            }))
            .catch(this.handleError);
    }
}
